import { useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { DialogShell } from '../ui/DialogShell';
import { ModalHeader } from '../ui/ModalHeader';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { ComprobanteInput } from './ComprobanteInput';
import { useComprobanteFormulario } from '../../hooks/useComprobanteFormulario';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { comprobanteService } from '../../services/comprobanteService';
import type { EntidadDinero, Respaldo } from '../../types/comprobante';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { mensajeErrorDeNegocio } from '../../utils/erroresDeNegocio';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';

export interface ItemParaAdjuntar {
  readonly movimientoId: string;
  readonly respaldo: Respaldo;
  readonly descripcion: string | null;
  readonly monto: number;
  readonly fecha: string;
}

interface AdjuntarComprobanteModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly entidad: EntidadDinero;
  readonly items: readonly ItemParaAdjuntar[];
  readonly modo?: 'adjuntar' | 'reemplazar';
}

export const AdjuntarComprobanteModal: React.FC<AdjuntarComprobanteModalProps> = ({
  isOpen, onClose, onSuccess, entidad, items, modo = 'adjuntar',
}) => {
  const [guardando, setGuardando] = useState(false);
  const { error, clearError, handleError } = useErrorHandler();
  const comprobante = useComprobanteFormulario({
    entidad, fechaMovimiento: null, permiteSubirDespues: false, pideMedioPago: false,
    obligatoriedad: 'siempre', mensajeFaltante: 'Adjunta el comprobante.',
  });
  const closeRef = useRef<(() => void) | null>(null);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { handleError(errorComprobante); return; }
    try {
      setGuardando(true);
      const { comprobante_id } = await comprobante.resolver();
      if (!comprobante_id) { handleError('Adjunta el comprobante.'); return; }
      await comprobanteService.adjuntar(items, comprobante_id);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'No pudimos adjuntar el comprobante. Intenta nuevamente.');
    } finally {
      setGuardando(false);
    }
  };

  const titulo = modo === 'reemplazar' ? 'Reemplazar comprobante' : 'Adjuntar comprobante';

  return (
    <DialogShell
      isOpen={isOpen} onClose={onClose} size="md" level="nested"
      hasUnsavedChanges={guardando || comprobante.hayCambios} closeRef={closeRef}
      header={<ModalHeader title={titulo} icon={<Paperclip className="w-6 h-6 text-blue-600" />} onClose={() => closeRef.current?.()} />}
    >
      <form onSubmit={guardar} className="space-y-5">
        <FormErrorAlert error={error} onClear={clearError} />
        <ComprobanteInput comprobante={comprobante} deshabilitado={guardando} />
        <div>
          <p className="text-sm font-medium text-slate-600 mb-1">
            {items.length === 1 ? 'Para el movimiento' : `Para ${items.length} movimientos`}
          </p>
          <ul className="text-sm text-slate-600 space-y-0.5">
            {items.map((i) => (
              <li key={`${i.movimientoId}:${i.respaldo}`}>
                {formatDate(i.fecha)} · {i.descripcion ?? 'Sin descripción'} · {formatCurrency(i.monto)}
                {i.respaldo === 'reembolso' ? ' (reembolso)' : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex gap-3 pt-4 border-t border-slate-200">
          <button type="button" onClick={() => closeRef.current?.()} disabled={guardando}
            className="flex-1 min-h-11 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={guardando || comprobante.subiendo}
            className="flex-1 min-h-11 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {guardando ? 'Guardando…' : titulo}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
